import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "../../components/TopBar";
import EmptyState from "../../components/EmptyState";
import { Modal, Field } from "../Fields";
import { api, fmtUSD, fmtNum } from "../../lib/api";
import { useYear } from "../../context/YearContext";
import { Truck, Plus, Pencil, Trash2, BarChart3 } from "lucide-react";
import { toast } from "sonner";

const KINDS = [{ value: "truck", label: "Truck" }, { value: "trailer", label: "Trailer" }];
const STATUSES = [{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }, { value: "sold", label: "Sold" }];

const blank = { kind: "truck", nickname: "", year: "", make: "", model: "", vin: "", license_plate: "", purchase_date: "", purchase_cost: "", status: "active", notes: "" };

export default function Fleet({ businessId }) {
  const { year } = useYear();
  const [summary, setSummary] = useState({ units: [], totals: {} });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = async () => {
    const { data } = await api.get(`/fleet/summary?business_id=${businessId}&year=${year}`);
    setSummary(data);
  };

  useEffect(() => { load(); }, [businessId, year]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      business_id: businessId,
      year: parseInt(form.year) || 0,
      purchase_cost: parseFloat(form.purchase_cost) || 0,
    };
    try {
      if (editing) await api.put(`/fleet-units/${editing.id}`, payload);
      else await api.post(`/fleet-units`, payload);
      toast.success(editing ? "Unit updated" : "Unit added");
      setOpen(false); setEditing(null); setForm(blank); load();
    } catch { toast.error("Could not save unit"); }
  };

  const onEdit = (u) => {
    setEditing(u);
    setForm({ ...blank, ...u, year: u.year || "", purchase_cost: u.purchase_cost || "" });
    setOpen(true);
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this fleet unit?\nNote: loads/expenses linked to it will keep the reference but lose the name.")) return;
    await api.delete(`/fleet-units/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div>
      <TopBar
        title="Fleet"
        subtitle={`Each truck and trailer — what each one is earning and costing you in ${year}.`}
        actions={
          <div className="flex flex-wrap items-center gap-2 mt-6">
            <button data-testid="add-fleet-btn" onClick={() => { setEditing(null); setForm(blank); setOpen(true); }} className="bg-[#BC4727] text-white px-5 py-2 font-bold uppercase tracking-wider text-sm hover:bg-[#A03B1F] flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add Unit
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6" data-testid="fleet-totals">
        <Stat label="Units" value={summary.units.length} />
        <Stat label="Miles Billed" value={fmtNum(summary.totals.miles_billed || 0, 0)} />
        <Stat label="Revenue" value={fmtUSD(summary.totals.revenue || 0)} tone="positive" />
        <Stat label="Cost / Mile" value={fmtUSD(summary.totals.cost_per_mile || 0)} tone="warning" />
        <Stat label="Net / Mile" value={fmtUSD((summary.totals.revenue_per_mile || 0) - (summary.totals.cost_per_mile || 0))} tone="highlight" testid="fleet-net-per-mile" />
      </div>

      {summary.units.length === 0 ? (
        <EmptyState icon={Truck} title="No fleet units yet" hint="Add a truck or trailer to start tracking $/mile." testid="empty-fleet" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="fleet-grid">
          {summary.units.map((u) => <FleetCard key={u.id} unit={u} onEdit={onEdit} onDelete={onDelete} />)}
        </div>
      )}

      {open && (
        <Modal title={editing ? "Edit Fleet Unit" : "New Truck / Trailer"} onClose={() => setOpen(false)}>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="fleet-form">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Kind" required>
                <select data-testid="fleet-kind-select" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
                  {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                </select>
              </Field>
              <Field label="Nickname / ID" required>
                <input data-testid="fleet-nickname-input" required value={form.nickname} onChange={(e) => setForm({ ...form, nickname: e.target.value })} placeholder="Pete 12 / Reefer 03" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Year"><input data-testid="fleet-year-input" type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" /></Field>
              <Field label="Make"><input data-testid="fleet-make-input" value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
              <Field label="Model"><input data-testid="fleet-model-input" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="VIN"><input data-testid="fleet-vin-input" value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
              <Field label="License Plate"><input data-testid="fleet-plate-input" value={form.license_plate} onChange={(e) => setForm({ ...form, license_plate: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Purchase Date"><input data-testid="fleet-purchase-date" type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
              <Field label="Purchase Cost"><input data-testid="fleet-purchase-cost" type="number" step="0.01" value={form.purchase_cost} onChange={(e) => setForm({ ...form, purchase_cost: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" /></Field>
              <Field label="Status">
                <select data-testid="fleet-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
                  {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Notes"><textarea data-testid="fleet-notes-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="fleet-cancel-btn" onClick={() => setOpen(false)} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" data-testid="fleet-submit-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128]">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function Stat({ label, value, tone, testid }) {
  const color = tone === "positive" ? "text-[#1A4135]" : tone === "warning" ? "text-[#BC4727]" : tone === "highlight" ? "text-[#1A4135]" : "text-[#1F1E1B]";
  return (
    <div className="bg-white border border-[#1F1E1B] p-4" data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">{label}</div>
      <div className={`font-display text-2xl font-black tabular mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function FleetCard({ unit, onEdit, onDelete }) {
  const navigate = useNavigate();
  const Icon = unit.kind === "trailer" ? BarChart3 : Truck;
  return (
    <div className="bg-white border border-[#1F1E1B] p-5" data-testid={`fleet-card-${unit.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 bg-[#1F1E1B] flex items-center justify-center">
            <Icon className="w-4 h-4 text-white" />
          </div>
          <div className="min-w-0">
            <h3 className="font-display text-lg font-bold truncate">{unit.nickname}</h3>
            <div className="text-xs text-[#66645E] truncate">
              {unit.year || ""} {unit.make || ""} {unit.model || ""} · {unit.kind} · <span className="uppercase">{unit.status}</span>
            </div>
          </div>
        </div>
        <div className="flex gap-1.5 flex-shrink-0">
          <button data-testid={`details-fleet-${unit.id}`} onClick={() => navigate(`./${unit.id}`)} title="Detail page" className="p-1.5 border border-[#1A4135] text-[#1A4135] hover:bg-[#1A4135] hover:text-white"><BarChart3 className="w-3 h-3" /></button>
          <button data-testid={`edit-fleet-${unit.id}`} onClick={() => onEdit(unit)} className="p-1.5 border border-[#1F1E1B] hover:bg-[#1F1E1B] hover:text-white"><Pencil className="w-3 h-3" /></button>
          <button data-testid={`delete-fleet-${unit.id}`} onClick={() => onDelete(unit.id)} className="p-1.5 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"><Trash2 className="w-3 h-3" /></button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#66645E]">Loads</div>
          <div className="font-bold tabular">{unit.load_count}</div>
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#66645E]">Miles Billed</div>
          <div className="font-bold tabular">{fmtNum(unit.miles_billed, 0)}</div>
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#66645E]">Revenue</div>
          <div className="font-bold tabular text-[#1A4135]">{fmtUSD(unit.revenue)}</div>
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#66645E]">Expenses</div>
          <div className="font-bold tabular text-[#BC4727]">{fmtUSD(unit.expenses)}</div>
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#66645E]">Cost / Mile</div>
          <div className="font-bold tabular">{fmtUSD(unit.cost_per_mile)}</div>
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#66645E]">Rev / Mile</div>
          <div className="font-bold tabular">{fmtUSD(unit.revenue_per_mile)}</div>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-[#1F1E1B] flex items-baseline justify-between">
        <span className="text-xs font-bold uppercase tracking-wider">Profit</span>
        <span className={`font-display text-2xl font-black tabular ${unit.profit >= 0 ? "text-[#1A4135]" : "text-[#BC4727]"}`} data-testid={`fleet-profit-${unit.id}`}>{fmtUSD(unit.profit)}</span>
      </div>
    </div>
  );
}
